// ── Auth: Google sign-in / sign-out + a tiny React hook ──────────────────────
import { useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  signOut as fbSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { auth, isFirebaseConfigured } from './config';
import { nativeGoogleIdToken } from '../capacitor/nativeGoogleAuth';
import { disconnect as gcalDisconnect, clearAccountHint } from '../google/calendar';
import { apiUrl } from './apiUrl';

const googleProvider = new GoogleAuthProvider();
// Always let the user pick which Google account (nicer on shared devices).
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Popup-first: it's the most reliable flow on the web AND in an installed PWA,
// because redirect sign-in depends on cross-domain storage (app on vercel.app,
// auth handler on firebaseapp.com) that modern browsers now partition/block —
// which causes the "bounced back to the login screen" bug. We only fall back to
// redirect if the environment genuinely can't open a popup (e.g. some webviews).
export async function signInWithGoogle() {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  if (Capacitor.isNativePlatform()) {
    try {
      const idToken = await nativeGoogleIdToken();
      const cred = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
      return cred.user;
    } catch (err) {
      if (err?.code === 'USER_CANCELLED') return null; // user backed out of the picker
      throw err;
    }
  }
  try {
    const cred = await signInWithPopup(auth, googleProvider);
    return cred.user;
  } catch (err) {
    // Genuine webview / environment that can't open a popup at all → redirect is
    // the only option. (Distinct from a *blocked* popup, handled below.)
    if (err?.code === 'auth/operation-not-supported-in-this-environment') {
      await signInWithRedirect(auth, googleProvider);
      return null; // result arrives after redirect, via getRedirectResult
    }
    // Popup was blocked by the browser. On mobile the redirect fallback often
    // can't persist the session (cross-domain storage partitioning), so instead
    // of silently bouncing the user into a broken flow, re-throw the code so the
    // UI can show "allow pop-ups" guidance. Re-enabling pop-ups restores the
    // reliable first-party sign-in.
    if (err?.code === 'auth/popup-blocked') {
      throw err;
    }
    // User closed the popup themselves — not an error worth surfacing.
    if (
      err?.code === 'auth/cancelled-popup-request' ||
      err?.code === 'auth/popup-closed-by-user'
    ) {
      return null;
    }
    throw err;
  }
}

// Explicit redirect sign-in — offered as a fallback for users who genuinely
// can't (or don't want to) re-enable pop-ups.
export async function signInWithGoogleViaRedirect() {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  await signInWithRedirect(auth, googleProvider);
}

// ── Email / password ─────────────────────────────────────────────────────────
export async function signUpWithEmail(email, password, displayName) {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
  if (displayName) {
    await updateProfile(cred.user, { displayName: displayName.trim() });
  }
  return cred.user;
}

export async function signInWithEmail(email, password) {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  return cred.user;
}

export async function resetPassword(email) {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  await sendPasswordResetEmail(auth, email.trim());
}

// Turn a Firebase auth error code into something friendly for users.
export function friendlyAuthError(err) {
  switch (err?.code) {
    case 'auth/invalid-email':         return 'That email doesn’t look right.';
    case 'auth/missing-password':      return 'Please enter your password.';
    case 'auth/weak-password':         return 'Password should be at least 6 characters.';
    case 'auth/email-already-in-use':  return 'That email already has an account. Try logging in.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':        return 'Wrong email or password.';
    case 'auth/too-many-requests':     return 'Too many tries. Please wait a moment and retry.';
    case 'auth/network-request-failed':return 'Network issue. Check your connection.';
    default:                           return 'Something went wrong. Please try again.';
  }
}

export async function signOutUser() {
  if (!isFirebaseConfigured) return;
  // The Google Calendar/Tasks connection is a browser-wide cookie, not scoped
  // to whichever Clarity account is signed in — without this, signing into a
  // different Clarity account on the same browser would silently inherit the
  // previous account's Google connection instead of prompting to reconnect.
  await gcalDisconnect();
  // Also forget which Google account to silently pre-select on next connect —
  // otherwise a stale hint from this account skips the chooser and connects
  // the next Clarity account to the WRONG Google account with no prompt.
  clearAccountHint();
  await fbSignOut(auth);
}

// Permanently deletes the signed-in user's account (Firestore doc + any
// connected Google Calendar token + the Firebase Auth user itself), via the
// server-side /api/account/delete endpoint. Does NOT sign the user out
// locally or touch localStorage — callers should do that once this resolves.
export async function deleteAccountEverywhere() {
  if (!isFirebaseConfigured) throw new Error('Firebase not configured');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const idToken = await user.getIdToken();
  const res = await fetch(apiUrl('/api/account/delete'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Failed to delete account');
  }
}

// React hook: { user, loading }.
// - When Firebase isn't configured, returns { user: null, loading: false }
//   immediately so the app renders normally in local-only mode.
export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(isFirebaseConfigured);

  useEffect(() => {
    if (!isFirebaseConfigured) return;
    let active = true;
    let unsub = () => {};
    (async () => {
      // Finish any pending redirect sign-in BEFORE we wire up the auth listener,
      // so a user returning from the Google redirect has their session committed
      // to persistence first and isn't briefly bounced to the login screen.
      try { await getRedirectResult(auth); } catch { /* ignore — no pending redirect */ }
      if (!active) return;
      unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
        setLoading(false);
      });
    })();
    return () => { active = false; unsub(); };
  }, []);

  return { user, loading };
}
